import { Component, OnInit, OnDestroy, AfterViewChecked, AfterViewInit, HostListener, ChangeDetectorRef, Renderer2, Inject } from '@angular/core';
import { Router, Event, NavigationStart, NavigationEnd } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { filter, tap } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { DOCUMENT } from '@angular/common';
import { AngularPaths, Module, Permission, UserSession } from '@models/index.model';
import { AuthService, UtilsService, PermissionsService, ModulesService, SigntrustService, NavigateService, SessionKeepAliveService } from '@services/index.service';
import { MsalService } from '@azure/msal-angular';
import { App } from '@capacitor/app';
import { environment } from '@environments/environment';

declare var gtag;

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false
})
export class AppComponent implements OnInit, OnDestroy, AfterViewInit, AfterViewChecked {

  favIcon: HTMLLinkElement | null;
  public theme = environment.THEME;
  public currentVersion = environment.VERSION;
  public currentYear: number;
  public thirdPartiesLicences = `${window.location.origin}/legal/THIRD_PARTY_NOTICES.txt`;
  public hideNavbar = true;
  public setLanguage = 'es';
  public closeAppMsg = '';
  public showAfterAuth: boolean;
  public currentSession: UserSession | null;
  public pantResolution: number;
  public componentLoadding: boolean;
  public authorizations: any;
  public spDomainConfig: any;
  public googleAnalyticsToken: string = '';

  private readonly _subs = new Subscription();

  private _routesWithoutNavbar: string[] = [
    `/${AngularPaths.LOGIN}`, `/${AngularPaths.PASSWORD_UPDATE}`,
    `/${AngularPaths.NOT_FOUND}`, `/${AngularPaths.SSO_USER_PIN}`,
    `/${AngularPaths.USER_PIN}`, `/${AngularPaths.CONFIG_USER_PIN}`,
    `/${AngularPaths.MSAL_LOGIN_FAILED}`, `/${AngularPaths.ERROR}`
  ];

  constructor(
    private router: Router,
    private _navigate: NavigateService,
    public _auth: AuthService,
    public translate: TranslateService,
    private cdRef: ChangeDetectorRef,
    public _utils: UtilsService,
    private msalAuthService: MsalService,
    private _signtrustServ: SigntrustService,
    private _permiss: PermissionsService,
    private _modules: ModulesService,
    private renderer: Renderer2,
    private _keepAlive: SessionKeepAliveService,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.theme = this._utils.titleCaseWord(this.theme);
    this.currentYear = new Date().getFullYear();
    this.favIcon = this.document.querySelector('#favIcon');
    this._initializeTheme();
    this._initializeRouterEvents();

    this.componentLoadding = true;
    this.pantResolution = window.innerWidth;
    this.googleAnalyticsToken = '';
  }

  ngOnInit() {
    // Mantiene viva la sesión en segundo plano mientras el refreshToken sea válido.
    this._keepAlive.start();

    this.translate.get('GLOBAL.CLOSE-APP-MSG').subscribe(res => {
      this.closeAppMsg = res;
    });

    // Reemplazamos jQuery con Renderer2
    const fakeFileClick = this.document.querySelector('.fake-file-click');
    if (fakeFileClick) {
      this.renderer.listen(fakeFileClick, 'dragover', () => {
        this.renderer.addClass(fakeFileClick, 'drag-over');
      });
      this.renderer.listen(fakeFileClick, 'dragleave', () => {
        this.renderer.removeClass(fakeFileClick, 'drag-over');
      });
    }

    if (environment.platform === 'mobile') {
      this._configureMobile();
    }
  }

  ngAfterViewInit() {
    // Toggle del loader: inmediato en cada navegación (comportamiento previo:
    // el router-outlet se muestra en cuanto arranca la navegación).
    this._subs.add(
      this.router.events
        .pipe(filter((event: Event): event is NavigationStart => event instanceof NavigationStart))
        .subscribe(() => this.checkUserConfig())
    );
  }

  ngOnDestroy() {
    this._subs.unsubscribe();
    this._keepAlive.stop();
  }

  ngAfterViewChecked() {
    const inputElements = this.document.getElementsByTagName('input');
    for (let i = 0; i < inputElements.length; i++) {
      if (inputElements[i].classList.contains('disableAutoComplete')) {
        inputElements[i].setAttribute('autocomplete', 'off');
      }
    }
    this.cdRef.detectChanges();
  }

  @HostListener('window:resize', ['$event'])
  onResize() {
    this.pantResolution = window.innerWidth;
  }

  private _initializeTheme() {
    this.theme = this._utils.titleCaseWord(this.theme);
    if (this.favIcon) {
      this.favIcon.href = `assets/images/favicon/${this.theme}/favicon.ico`;
    }

    const htmlElement = this.document.documentElement;

    if (this.theme === 'Redtrust') {
      this.renderer.addClass(htmlElement, 'rt-theme');
    } else {
      this.renderer.removeClass(htmlElement, 'rt-theme');
    }
  }

  private _initializeRouterEvents() {
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationStart),
        tap((event: NavigationStart) => {
          if (event.url.includes(`/${AngularPaths.MSAL_LOGOUT}`)) {
            this.msalAuthService.logoutRedirect();
          }
          this._setBodyBackground(event.url);
        })
      ).subscribe();

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        tap((event: NavigationEnd) => {
          if (this.googleAnalyticsToken) {
            gtag('config', this.googleAnalyticsToken, {
              'page_path': event.urlAfterRedirects
            });
          }
          this._setBodyBackground(event.url);
        })
      ).subscribe();
  }

  private _configureMobile() {
    App.addListener('appUrlOpen', data => {
      localStorage.setItem('appUrlOpen', data.url);
      this._signtrustServ.setFile(data.url);
      if (this._modules.hasModule(Module.WEB_SIGNATURE) && this._permiss.hasPermiss(Permission.WEB_SIGNATURE)) {
        this._navigate.setUrl(`/${AngularPaths.SIGNTRUST}`);
      }
    });

    App.addListener('backButton', ({ canGoBack }) => {
      App.removeAllListeners();
      if (confirm(this.closeAppMsg)) {
        localStorage.setItem('docOpen', '0');
        App.exitApp();
      } else {
        this._configureMobile();
        window.history.back();
      }
    });

    App.addListener('appStateChange', ({ isActive }) => {
      console.log('App state changed. Is active?', isActive);
    });

    App.addListener('appUrlOpen', data => {
      console.log('App opened with URL:', data);
    });

    App.addListener('appRestoredResult', data => {
      console.log('Restored state:', data);
    });
  }

  public checkUserConfig() {
    // Solo gestiona el estado de carga (el outlet se muestra al arrancar la navegación).
    this.componentLoadding = false;
  }

  private _setBodyBackground(routerUrl: string): void {
    this.hideNavbar = this._routesWithoutNavbar.some(route => routerUrl.includes(route));

    const appBody = this.document.querySelector('.app-body');
    if (appBody) {
      if (this.hideNavbar) {
        this.renderer.removeClass(appBody, 'content-bg');
        this.renderer.addClass(appBody, 'login-bg');
      } else {
        this.renderer.removeClass(appBody, 'login-bg');
        this.renderer.addClass(appBody, 'content-bg');
      }
    }
  }
}
